import React, { useState, useMemo, useEffect } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Loader from "../../../../components/Common/Loader";
import {
  SIGNUPQUESTIONUPDATE,
  SIGNUPQUESTIONADD,
  SIGNUPQUESTIONDETAIL,
} from "../../../../services/ApiCalls";
import { catchAsync, checkResponse } from "../../../../utilities/utilities";

const questionSchema = z.object({
  name: z.string({ required_error: "Question name is required" }),
  questionType: z.enum(["SINGLE", "MULTIPLE"], {
    required_error: "Question type is required",
  }),
  description: z.string().optional(),
  answers: z
    .array(z.string().min(1, "Answer cannot be empty"))
    .min(1, "At least one answer is required"),
});

const AddSignUpQuestion = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [questionDetails, setQuestionDetails] = useState(null);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(questionSchema),
    defaultValues: {
      name: "",
      questionType: "SINGLE",
      description: "",
      answers: [""],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "answers",
  });

  const submitHandler = catchAsync(async (data) => {
    const body = {
      ...data,
      ...(id && { _id: id }),
    };

    const res = await (id
      ? SIGNUPQUESTIONUPDATE(body)
      : SIGNUPQUESTIONADD(body));
    const success = checkResponse({ res, showSuccess: true });

    if (success) {
      navigate("/signupquestion");
    }
  });

  const getData = catchAsync(async () => {
    setLoader(true);
    const res = await SIGNUPQUESTIONDETAIL(id);

    const success = checkResponse({
      res,
      setData: setQuestionDetails,
      setLoader,
    });

    if (success && res?.data) {
      reset(res?.data?.data);
    }
  }, setLoader);

  useEffect(() => {
    if (id) getData();
  }, [id]);

  const handleCancel = () => {
    navigate("/signupquestion");
  };

  return (
    <>
      {loader && <Loader />}
      <section className="addPromo position-relative py-3">
        <Container>
          <Row>
            <Col lg="12">
              <div className="d-flex align-items-center gap-10">
                <Link
                  to="/signupquestion"
                  className="border d-flex align-items-center p-2 rounded"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 10 16"
                    fill="none"
                  >
                    <path
                      d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
                      fill="#1E232C"
                      stroke="#1E232C"
                      stroke-width="0.2"
                    />
                  </svg>
                </Link>
                <h4 className="mb-0 py-3 fw-bold themeBlue text-capitalize">
                  {id ? "Edit SignUp Question" : "Add SignUp Question"}
                </h4>
              </div>
            </Col>

            <Col lg="12" className="my-2">
              <div
                className="formWrpper px-lg-5 p-md-4 p-3 rounded"
                style={{ border: "1px solid #dee2e6" }}
              >
                <Form onSubmit={handleSubmit(submitHandler)}>
                  <Row className="justify-content-between">
                    <Col lg="5" md="6" className="my-2">
                      <div className="py-2">
                        <label className="form-label fw-sbold text-muted ps-2 m-0">
                          Question Name
                        </label>
                        <input
                          type="text"
                          placeholder="Enter question"
                          className="form-control"
                          {...register("name")}
                        />
                        {errors?.name && (
                          <p className="text-danger m-0">
                            {errors.name.message}
                          </p>
                        )}
                      </div>

                      <div className="py-2">
                        <label className="form-label fw-sbold text-muted ps-2 m-0">
                          Description
                        </label>
                        <textarea
                          rows={5}
                          className="form-control h-auto"
                          {...register("description")}
                        ></textarea>
                        {errors?.description && (
                          <p className="text-danger m-0">
                            {errors.description.message}
                          </p>
                        )}
                      </div>
                    </Col>

                    <Col lg="5" md="6" className="my-2">
                      <div className="py-2">
                        <label className="form-label fw-sbold text-muted ps-2 m-0">
                          Question Type
                        </label>
                        <Form.Select {...register("questionType")}>
                          <option value="SINGLE">Single Choice</option>
                          <option value="MULTIPLE">Multiple Choice</option>
                        </Form.Select>
                        {errors?.questionType && (
                          <p className="text-danger m-0">
                            {errors.questionType.message}
                          </p>
                        )}
                      </div>

                      <div className="py-2">
                        <label className="form-label fw-sbold text-muted ps-2 m-0">
                          Answers
                        </label>
                        {fields.map((field, index) => (
                          <div key={field.id} className="d-flex gap-2 mb-2">
                            <input
                              type="text"
                              placeholder={`Answer ${index + 1}`}
                              className="form-control"
                              {...register(`answers.${index}`)}
                            />
                            <Button
                              variant="danger"
                              onClick={() => remove(index)}
                            >
                              ✕
                            </Button>
                          </div>
                        ))}
                        <Button
                          type="button" 
                          className="pt-2"
                          variant="secondary"
                          onClick={() => append("")}
                        >
                          + Add Answer
                        </Button>
                        {errors?.answers && (
                          <p className="text-danger m-0">
                            {errors.answers.message}
                          </p>
                        )}
                      </div>
                    </Col>

                    <Col lg="12" className="my-2">
                      <div className="d-flex align-items-center justify-content-center gap-10">
                        <Button
                          type="button"
                          onClick={handleCancel}
                          className="commonBtn GreyBtn"
                        >
                          Cancel
                        </Button>
                        <Button type="submit" className="commonBtn">
                          Submit
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default AddSignUpQuestion;
